import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { PRODUCT_STATUS_CONFIG, normalizeProductStatus } from './constants.js';
import {
  BILLING_EVIDENCE_DRAG_END,
  BILLING_EVIDENCE_DRAG_START,
  EVIDENCE_DRAG_TYPE,
} from './billingDragTypes.js';
import {
  compactEvidenceContext,
  DetailHeader,
  KeyValueGrid,
  PinnedCard,
  previewChipColor,
  previewStyle,
  visibleMeta,
} from './ExpandableLineItemParts.jsx';

const money = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  if (typeof n !== 'number') return String(n);
  return `$${Math.round(n).toLocaleString()}`;
};

const ProductWarningLane = ({ state, fallbackLabel }) => {
  const missingEvidence = Boolean(state?.missingEvidence || fallbackLabel);
  const missingAccount = Boolean(state?.missingAccount);
  const hasSuggestion = Boolean(state?.hasSuggestedMatch);
  const hasWarnings = missingEvidence || missingAccount;
  const hasState = hasWarnings || hasSuggestion;
  const hasDoubleWarning = missingEvidence && missingAccount;
  const title = hasState
    ? [
        missingEvidence ? 'Missing AppSheet/DPE evidence link' : '',
        missingAccount ? 'Missing Salesforce account ownership' : '',
        hasSuggestion ? `Suggested recurring evidence: ${state.suggestionLabel || 'available'}` : '',
        state?.hasConflictRisk ? 'Multiple Salesforce accounts exist for this BAC; choose one before export' : '',
        state?.confidenceLabel && !missingAccount ? `Account match: ${state.confidenceLabel}` : '',
        state?.explanation && !missingAccount ? state.explanation : '',
      ].filter(Boolean).join(' - ')
    : 'Evidence and Salesforce account are linked';

  return (
    <Tooltip title={title}>
      <Box
        aria-label={hasWarnings ? title : 'Linked product evidence and account'}
        role="img"
        sx={{
          alignSelf: 'stretch',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: hasDoubleWarning ? 40 : 32,
          minWidth: hasDoubleWarning ? 40 : 32,
          pr: hasDoubleWarning ? 1 : 0.85,
          mr: hasDoubleWarning ? 0.5 : 0.25,
          borderRight: '1px solid',
          borderColor: hasWarnings ? 'error.light' : hasSuggestion ? 'info.light' : 'divider',
          color: hasWarnings ? 'error.main' : hasSuggestion ? 'info.main' : 'transparent',
        }}
      >
        {missingEvidence && missingAccount ? (
          <>
            <LinkIcon sx={{ position: 'absolute', top: -1, left: -1, fontSize: 15 }} />
            <Box
              sx={{
                position: 'absolute',
                width: 19,
                height: 2,
                left: 8,
                top: 16,
                borderRadius: 1,
                bgcolor: 'error.main',
                transform: 'rotate(-45deg)',
                opacity: 0.82,
                pointerEvents: 'none',
              }}
            />
            <PersonOutlineOutlinedIcon sx={{ position: 'absolute', bottom: 0, right: 6, fontSize: 15 }} />
          </>
        ) : missingEvidence ? (
          <LinkOffIcon sx={{ fontSize: 18 }} />
        ) : missingAccount ? (
          <PersonOffOutlinedIcon sx={{ fontSize: 18 }} />
        ) : null}
        {hasSuggestion ? (
          <AutoAwesomeOutlinedIcon
            sx={{
              position: hasWarnings ? 'absolute' : 'static',
              bottom: hasWarnings ? 4 : 'auto',
              right: hasWarnings ? 4 : 'auto',
              fontSize: hasWarnings ? 12 : 18,
              color: 'info.main',
            }}
          />
        ) : null}
      </Box>
    </Tooltip>
  );
};

const billabilityState = (billability) => {
  if (billability && typeof billability === 'object') {
    const state = billability.state || (billability.billableForRound ? 'billable' : 'not_billable');
    if (!['billable', 'not_billable', 'billing_conflict'].includes(state)) return null;
    return {
      state,
      billable: state === 'billable',
      label: billability.reason || (state === 'billing_conflict'
        ? 'Billing conflict'
        : state === 'billable'
          ? 'Billable for this round'
          : 'Not billable for this round'),
    };
  }
  if (billability !== true && billability !== false) return null;
  return {
    state: billability ? 'billable' : 'not_billable',
    billable: billability,
    label: billability ? 'Billable for this round' : 'Not billable for this round',
  };
};

const BillabilityIcon = ({ billability }) => {
  const iconState = billabilityState(billability);
  if (!iconState) return null;
  const color = iconState.state === 'billing_conflict'
    ? 'warning.main'
    : iconState.billable
      ? 'success.main'
      : 'error.main';
  return (
    <Tooltip title={iconState.label}>
      <Box
        role="img"
        aria-label={iconState.label}
        data-billing-product-billable={iconState.state === 'billing_conflict' ? 'conflict' : iconState.billable ? 'true' : 'false'}
        data-billing-product-billability-state={iconState.state}
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1.5px solid',
          borderColor: color,
          color,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) => `${iconState.state === 'billing_conflict'
            ? theme.palette.warning.main
            : iconState.billable
              ? theme.palette.success.main
              : theme.palette.error.main}14`,
        }}
      >
        <AttachMoneyOutlinedIcon sx={{ fontSize: 13 }} />
      </Box>
    </Tooltip>
  );
};

/**
 * A line-item row that opens a Popover anchored to itself when clicked.
 * The popover has a Pin action — pinning promotes the popover to a
 * draggable floating card so it persists while the user opens other rows
 * to compare. Multiple rows can be pinned simultaneously.
 */
const ExpandableLineItem = ({
  primary,
  secondary,
  amount,
  amountProps = {},
  meta = [],
  muted = false,
  details = {},
  dense = false,
  status,
  varianceContext = null,
  onVarianceMenu = null,
  onEvidenceDrop = null,
  draftPreview = null,
  codeReplacementPreview = null,
  linkWarningLabel = '',
  warningState = null,
  hideWarningLane = false,
  compactCard = false,
  defaultOpen = false,
  billability = null,
}) => {
  const normalizedStatus = status ? normalizeProductStatus(status, '') : '';
  const statusCfg = normalizedStatus ? PRODUCT_STATUS_CONFIG[normalizedStatus] : null;
  const borderColor = statusCfg?.border || null;
  const [anchor, setAnchor] = useState(null);
  const [pinnedPosition, setPinnedPosition] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocusedFromContext, setIsFocusedFromContext] = useState(false);
  const itemRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const focusedIdentityRef = useRef('');

  const entries = Object.entries(details).filter(([, v]) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    return true;
  });
  const metaEntries = visibleMeta(meta);
  const hasDetails = entries.length > 0;
  const isOpen = Boolean(anchor);
  const isPinned = Boolean(pinnedPosition);
  const hasVarianceMenu = Boolean(onVarianceMenu && varianceContext);
  const canDragEvidence = Boolean(
    varianceContext
    && ['appsheet', 'dpe', 'gm', 'cpq'].includes(varianceContext.source)
    && (varianceContext.productCode || varianceContext.orderItemId || varianceContext.referenceId),
  );
  const canDropEvidence = Boolean(onEvidenceDrop && varianceContext);
  const draftStyle = previewStyle(draftPreview);
  const isDraftRemoved = draftPreview?.variant === 'removed';
  const isDraftReplaced = draftPreview?.variant === 'replaced';
  const shouldStrikePrimary = isDraftRemoved || isDraftReplaced;
  const shouldStrikeAmount = isDraftRemoved || Boolean(draftPreview?.proposedPrice || draftPreview?.proposedPrice === 0);
  const codeChangePreview = codeReplacementPreview?.original && codeReplacementPreview?.replacement
    ? codeReplacementPreview
    : draftPreview?.replacementProductCode && !isDraftRemoved
      ? { replacement: draftPreview.replacementProductCode }
      : null;
  const showWarningLane = !hideWarningLane && Boolean(warningState || linkWarningLabel);
  const invoiceWarning = warningState?.invoiceGroupWarning;
  const productIdentity = [
    varianceContext?.source,
    varianceContext?.productCode,
    varianceContext?.orderItemId,
    varianceContext?.referenceId,
    primary,
    secondary,
  ].filter(Boolean).join('|');

  const handleClick = (e) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (!hasDetails) return;
    setAnchor(anchor ? null : e.currentTarget);
  };

  useEffect(() => {
    setAnchor(null);
    setPinnedPosition(null);
    setIsFocusedFromContext(false);
  }, [productIdentity]);

  useEffect(() => {
    if (!defaultOpen || !itemRef.current || focusedIdentityRef.current === productIdentity) return undefined;
    focusedIdentityRef.current = productIdentity;
    itemRef.current.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    setIsFocusedFromContext(true);
    const timeout = window.setTimeout(() => setIsFocusedFromContext(false), 2800);
    return () => window.clearTimeout(timeout);
  }, [defaultOpen, productIdentity]);

  const handleVarianceMenu = useCallback((event) => {
    if (!hasVarianceMenu) return;
    event.preventDefault?.();
    event.stopPropagation?.();
    setAnchor(null);
    onVarianceMenu(event, { ...varianceContext, draftPreview });
  }, [draftPreview, hasVarianceMenu, onVarianceMenu, varianceContext]);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearLongPress();
  }, [clearLongPress]);

  const handlePointerDown = useCallback((event) => {
    if (!hasVarianceMenu || event.pointerType === 'mouse') return;
    clearLongPress();
    longPressTriggeredRef.current = false;
    const { clientX, clientY } = event;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      setAnchor(null);
      onVarianceMenu({
        clientX,
        clientY,
        preventDefault: () => {},
        stopPropagation: () => {},
      }, { ...varianceContext, draftPreview });
    }, 550);
  }, [clearLongPress, draftPreview, hasVarianceMenu, onVarianceMenu, varianceContext]);

  const handlePin = useCallback((e) => {
    e?.stopPropagation?.();
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPinnedPosition({
      top: Math.min(window.innerHeight - 240, rect.bottom + 8),
      left: Math.max(8, Math.min(window.innerWidth - 480, rect.left)),
    });
    setAnchor(null);
  }, [anchor]);

  const handleUnpin = () => setPinnedPosition(null);

  const handlePromoteToPinned = useCallback((event) => {
    if (!anchor) return;
    handlePin(event);
  }, [anchor, handlePin]);

  const handleDragStart = useCallback((event) => {
    if (!canDragEvidence) return;
    const payload = compactEvidenceContext(varianceContext);
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData(EVIDENCE_DRAG_TYPE, JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', `${payload.productName || payload.productCode || 'Evidence'} evidence`);
    window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_START, { detail: payload }));
  }, [canDragEvidence, varianceContext]);

  const handleDragEnd = useCallback(() => {
    setIsDragOver(false);
    window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!canDropEvidence) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, [canDropEvidence]);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleDrop = useCallback((event) => {
    if (!canDropEvidence) return;
    const rawPayload = event.dataTransfer.getData(EVIDENCE_DRAG_TYPE);
    if (!rawPayload) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    try {
      const sourceContext = JSON.parse(rawPayload);
      onEvidenceDrop(sourceContext, compactEvidenceContext(varianceContext));
      window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
    } catch {
      // Ignore malformed drag payloads from outside the billing drawer.
    }
  }, [canDropEvidence, onEvidenceDrop, varianceContext]);

  return (
    <>
      <Stack
        ref={itemRef}
        direction="row"
        alignItems="flex-start"
        spacing={compactCard ? 0.5 : 1}
        onClick={handleClick}
        onContextMenu={handleVarianceMenu}
        onPointerDown={handlePointerDown}
        onPointerUp={clearLongPress}
        onPointerLeave={clearLongPress}
        onPointerCancel={clearLongPress}
        draggable={canDragEvidence}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        title={canDragEvidence ? 'Drag onto a product to inherit evidence, or onto a GM/CPQ file panel to add it there' : undefined}
        data-billing-product-card="true"
        data-billing-product-source={varianceContext?.source || undefined}
        data-billing-product-code={varianceContext?.productCode || secondary || undefined}
        data-billing-product-status={normalizedStatus || status || undefined}
        data-billing-product-density={dense ? 'compact' : 'comfortable'}
        data-billing-product-focused={isFocusedFromContext ? 'true' : undefined}
        sx={{
          cursor: canDragEvidence ? 'grab' : hasDetails ? 'pointer' : 'default',
          p: compactCard ? 0.6 : dense ? 0.75 : 1,
          minHeight: compactCard ? 56 : undefined,
          pl: borderColor ? (dense ? 1.25 : 1.5) : undefined,
          borderRadius: 1,
          bgcolor: isOpen && !borderColor
            ? 'primary.lighter'
            : isPinned
              ? 'warning.lighter'
              : isFocusedFromContext
                ? 'action.selected'
                : (muted ? 'transparent' : 'action.hover'),
          backgroundColor: isOpen && borderColor
            ? (theme) => `${borderColor}${theme.palette.mode === 'dark' ? '33' : '18'}`
            : undefined,
          border: '1px',
          borderStyle: muted ? 'dashed' : 'solid',
          borderColor: isOpen
            ? 'primary.main'
            : isPinned
              ? 'warning.main'
              : isDragOver
                ? 'info.main'
                : isFocusedFromContext
                  ? 'info.main'
                  : (muted ? 'divider' : 'transparent'),
          ...(draftPreview ? draftStyle : {}),
          borderLeft: draftPreview || borderColor ? '4px solid' : undefined,
          borderLeftColor: isDragOver ? 'info.main' : draftPreview ? draftStyle.borderLeftColor : borderColor,
          touchAction: hasVarianceMenu ? 'manipulation' : undefined,
          opacity: draftPreview ? draftStyle.opacity : muted && !isOpen && !isPinned ? 0.85 : 1,
          transition: 'background-color 80ms, border-color 80ms',
          '&:hover': hasDetails
            ? {
                bgcolor: draftPreview ? draftStyle.bgcolor : isOpen && !borderColor ? 'primary.lighter' : isPinned ? 'warning.lighter' : 'action.selected',
                backgroundColor: isOpen && borderColor
                  ? (theme) => `${borderColor}${theme.palette.mode === 'dark' ? '40' : '22'}`
                  : undefined,
                borderColor: draftPreview ? draftStyle.borderColor : isOpen ? 'primary.main' : isPinned ? 'warning.main' : 'primary.light',
                borderLeft: draftPreview || borderColor ? '4px solid' : undefined,
                borderLeftColor: draftPreview ? draftStyle.borderLeftColor : borderColor,
              }
            : undefined,
        }}
      >
        {showWarningLane && <ProductWarningLane state={warningState} fallbackLabel={linkWarningLabel} />}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: compactCard ? 1.2 : 1.3, minWidth: 0, fontSize: compactCard ? 11.2 : undefined }}
              noWrap
            >
              <Box component="span" sx={{ textDecoration: shouldStrikePrimary ? 'line-through' : 'none' }}>
                {primary}
              </Box>
            </Typography>
            {draftPreview?.label && (
              <Chip
                size="small"
                label={draftPreview.label}
                color={previewChipColor(draftPreview.variant)}
                variant="outlined"
                sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 9.5, fontWeight: 800 } }}
              />
            )}
            {codeReplacementPreview?.original && codeReplacementPreview?.replacement && (
              <Chip
                size="small"
                label={codeReplacementPreview.label || 'Inherited evidence'}
                color="info"
                variant="outlined"
                sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 9.5, fontWeight: 800 } }}
              />
            )}
          </Stack>
          {secondary && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                fontSize: 10.5,
                display: metaEntries.length ? 'none' : 'block',
                textDecoration: shouldStrikePrimary ? 'line-through' : 'none',
              }}
              noWrap
            >
              {secondary}
            </Typography>
          )}
          {metaEntries.length > 0 && (
            <Stack direction="row" spacing={compactCard ? 0.35 : 0.75} useFlexGap flexWrap={compactCard ? 'nowrap' : 'wrap'} sx={{ mt: 0.35, minWidth: 0 }}>
              {metaEntries.map((item, index) => {
                const label = typeof item === 'string' ? null : item.label;
                const value = typeof item === 'string' ? item : item.value;
                const isCodeChangeSlot = index === 0 && codeChangePreview?.replacement;
                const originalCode = codeChangePreview?.original || value;
                return (
                  <Typography
                    key={`${label || 'meta'}-${value}-${index}`}
                    variant="caption"
                    sx={{
                      color: isCodeChangeSlot ? 'info.dark' : 'text.secondary',
                      fontFamily: typeof item !== 'string' && item.mono === false ? 'inherit' : 'monospace',
                      fontSize: 10.5,
                      lineHeight: 1.35,
                      overflowWrap: 'anywhere',
                      whiteSpace: compactCard ? 'nowrap' : undefined,
                      overflow: compactCard ? 'hidden' : undefined,
                      textOverflow: compactCard ? 'ellipsis' : undefined,
                      textDecoration: shouldStrikePrimary && !isCodeChangeSlot ? 'line-through' : 'none',
                      fontWeight: isCodeChangeSlot ? 800 : undefined,
                    }}
                  >
                    {isCodeChangeSlot ? (
                      <>
                        <Box component="span" data-billing-inherited-original-code={codeReplacementPreview?.original ? 'true' : undefined} data-billing-draft-original-code={!codeReplacementPreview?.original ? 'true' : undefined} sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                          {originalCode}
                        </Box>
                        {' → '}
                        {codeChangePreview.replacement}
                      </>
                    ) : label ? `${label} ${value}` : value}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </Box>
        {isPinned && (
          <Tooltip title="Pinned — click row for a new popover or use the card's ⋮⋮ to move it">
            <PushPinOutlinedIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </Tooltip>
        )}
        {invoiceWarning && (
          <Tooltip title={invoiceWarning.label}>
            <WarningAmberOutlinedIcon
              data-billing-invoice-group-warning="true"
              color="warning"
              fontSize="small"
              sx={{ flexShrink: 0, mt: -0.15 }}
            />
          </Tooltip>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.25}
          sx={{ flexShrink: 0, alignSelf: 'stretch', minWidth: compactCard ? 74 : hasVarianceMenu ? 112 : 78 }}
          data-billing-line-trailing="true"
        >
          <Box sx={{ flexShrink: 0, textAlign: 'right', width: compactCard ? 58 : 88 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, minHeight: 20 }}>
              <BillabilityIcon billability={billability} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', textDecoration: shouldStrikeAmount ? 'line-through' : 'none', color: shouldStrikeAmount ? 'text.secondary' : undefined, ...amountProps }}
              >
                {typeof amount === 'number' ? money(amount) : amount}
              </Typography>
            </Box>
            {(draftPreview?.proposedPrice || draftPreview?.proposedPrice === 0) && (
              <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: 'warning.dark', lineHeight: 1.1 }}>
                {money(draftPreview.proposedPrice)}
              </Typography>
            )}
          </Box>
          {hasVarianceMenu && (
            <Tooltip title="Variance actions">
              <IconButton
                size="small"
                onClick={handleVarianceMenu}
                aria-label={`Variance actions for ${primary}`}
                sx={{ mr: compactCard ? -0.75 : -0.5, width: compactCard ? 24 : 28, height: compactCard ? 24 : 28 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Popover
        open={isOpen}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 460,
              minWidth: 320,
              p: 2,
              mt: 0.5,
              boxShadow: 6,
              borderTop: '3px solid',
              borderColor: 'primary.main',
            },
          },
        }}
      >
        <Stack spacing={1.5}>
          <DetailHeader
            primary={primary}
            secondary={secondary}
            dragHandleProps={{ onPointerDown: handlePromoteToPinned }}
            actions={
              <>
                <Tooltip title="Pin and move">
                  <IconButton size="small" onClick={handlePin} aria-label={`Pin and move ${primary}`}>
                    <PushPinOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton size="small" onClick={() => setAnchor(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
          <Divider />
          <KeyValueGrid entries={entries} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Drag the handle or pin to move this card
          </Typography>
        </Stack>
      </Popover>

      {isPinned && (
        <PinnedCard
          position={pinnedPosition}
          onDrag={setPinnedPosition}
          onClose={handleUnpin}
          primary={primary}
          secondary={secondary}
          entries={entries}
        />
      )}
    </>
  );
};

export default ExpandableLineItem;
